import { Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { hotTopics } from "@/mocks/hotList";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line text-primary-600",
  down: "ri-arrow-down-line text-secondary-600",
  flat: "ri-subtract-line text-foreground-400",
};

const trendText: Record<string, string> = { up: "上升", down: "回落", flat: "持平" };

const topThree = hotTopics.slice(0, 3);
const restTopics = hotTopics.slice(3);

export default function HotPage() {
  useSeo({
    title: "今日热榜 | 全网热议话题实时排行 - 52吃瓜网",
    description:
      "52吃瓜网今日热榜实时呈现全网讨论度最高的话题排行，标注热度值与升降趋势，一屏看清当下最热的大瓜与热议事件。",
    keywords: "今日热榜,实时热度榜,话题排行,52吃瓜网,全网热议,最新大瓜",
    path: "/hot",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "今日热榜 - 52吃瓜网",
        description: "全网热议话题实时排行，含热度值与升降趋势。",
        url: "https://kimchy.cn/hot",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://kimchy.cn/" },
          { "@type": "ListItem", position: 2, name: "今日热榜", item: "https://kimchy.cn/hot" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "52吃瓜网今日热榜",
        itemListElement: hotTopics.map((topic) => ({
          "@type": "ListItem",
          position: topic.rank,
          url: `https://kimchy.cn/article/${topic.articleId}`,
          name: topic.title,
        })),
      },
    ],
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 px-4 py-8 md:px-8 md:py-10">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "今日热榜" }]} />
        <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="flex items-center gap-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-fire-line text-xl"></i>
              </span>
              今日热榜
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 font-label">
              榜单按一个统计周期内的讨论量、转发量与搜索关注度加权排序，每 10 分钟刷新一次。热度值用于比较话题之间的相对关注差异。
            </p>
          </div>
          <span className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50 px-3.5 py-2 text-xs font-label text-foreground-500">
            <i className="ri-refresh-line text-secondary-600"></i>
            数据更新于 2026-09-14 09:20
          </span>
        </div>
      </section>

      <section className="px-4 py-8 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {topThree.map((topic, i) => {
            const article = articles.find((item) => item.id === topic.articleId);
            return (
              <article
                key={topic.rank}
                className="relative flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-md font-heading text-lg font-black ${
                      i === 0
                        ? "bg-primary-500 text-background-50"
                        : i === 1
                          ? "bg-accent-500 text-accent-950"
                          : "bg-secondary-500 text-background-50"
                    }`}
                  >
                    {topic.rank}
                  </span>
                  <span className="flex items-center gap-1 text-xs font-label text-foreground-500">
                    <i className={`${trendIcon[topic.trend]} text-xs`}></i>
                    {trendText[topic.trend]}
                  </span>
                </div>
                <Link to={`/article/${topic.articleId}`} className="group mt-4">
                  <h2 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {topic.title}
                  </h2>
                </Link>
                {article ? (
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600 font-label">
                    {article.excerpt}
                  </p>
                ) : null}
                <div className="mt-auto flex items-center justify-between pt-4">
                  <span className="flex items-center gap-1 text-sm font-label font-medium text-primary-600">
                    <i className="ri-fire-line"></i>
                    {topic.heat}万
                  </span>
                  <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label text-foreground-500">
                    {topic.tag}
                  </span>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-4">
          <div className="lg:col-span-3">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 bg-background-100 px-5 py-3.5">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  完整榜单
                </h2>
                <span className="text-xs font-label text-foreground-400">第 4 - {hotTopics.length} 名</span>
              </div>
              <ul>
                {restTopics.map((topic) => (
                  <li
                    key={topic.rank}
                    className="border-b border-background-100 last:border-0"
                  >
                    <Link
                      to={`/article/${topic.articleId}`}
                      className="group flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-background-100"
                    >
                      <span className="w-6 shrink-0 text-center font-heading text-base font-black text-foreground-400">
                        {topic.rank}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-1 block text-sm font-label text-foreground-800 transition-colors group-hover:text-primary-700">
                          {topic.title}
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-x-3 text-xs font-label text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-fire-line text-primary-500"></i>
                            {topic.heat}万
                          </span>
                          <span className="flex items-center gap-1">
                            <i className={`${trendIcon[topic.trend]} text-xs`}></i>
                            {trendText[topic.trend]}
                          </span>
                        </span>
                      </span>
                      <span className="hidden shrink-0 rounded-full bg-secondary-50 px-2.5 py-1 text-xs font-label text-secondary-700 sm:block">
                        {topic.tag}
                      </span>
                      <i className="ri-arrow-right-s-line shrink-0 text-foreground-300"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-layout-grid-line text-secondary-600"></i>
                按分类看榜
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs font-label text-foreground-600 transition-colors hover:bg-background-200"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-information-line text-accent-600"></i>
                榜单说明
              </h3>
              <ul className="mt-3 flex flex-col gap-2.5 text-xs leading-relaxed text-foreground-600 font-label">
                <li>· 热度值反映相对关注差异，不代表绝对人数。</li>
                <li>· 上升 / 回落表示相较上一次刷新的变化方向。</li>
                <li>· 话题信息以当事方公开回应为准。</li>
              </ul>
            </div>
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 md:p-5">
              <h3 className="font-heading text-base font-bold text-primary-800">
                发现漏掉的热点？
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-primary-900/80 font-label">
                告诉我们你关注的话题，我们会评估是否纳入热榜追踪。
              </p>
              <Link
                to="/contact"
                className="mt-4 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
              >
                提交话题
              </Link>
            </div>
          </aside>
        </div>

        <div className="mt-12 border-t border-background-200 pt-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950">
            榜单内事件精读
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles.slice(0, 3).map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}